<script setup>
  import { computed, nextTick, onMounted, ref, watch } from 'vue';
  import { useI18n } from 'vue-i18n';
  import { fetchAllLibrariesOrRetailersByNetwork } from '../services/LibrariesRetailersService';
  import Modal from '../components/ui/Modal.vue';
  import UserModal from '../pages/UserModal.vue';
  import UserDeleteModal from '../pages/UserDeleteModal.vue';
  import UserAccessesModal from '../pages/UserAccessesModal.vue';
  import UserRemoveAccessModal from '../pages/UserRemoveAccessModal.vue';
  import UsersManagementTable from './UsersManagementTable.vue';
  import SearchableList from './SearchableList.vue';
  import { debounce } from '../utils/debounce.js';

  const { t } = useI18n();
  const i18nScope = ref('components.users_management');
  const usersManagementTable = ref(null);

  const props = defineProps({
    roles: {
      type: Array,
      required: true
    },
    networks: {
      type: Array,
      required: true
    },
    lang: {
      type: String,
      default: 'en'
    },
    userType: {
      type: String,
      default: 'user'
    },
    defaultNetwork: {
      type: Object,
      default: () => null
    },
    networkAdmin: {
      type: Boolean,
      default: false
    },
    defaultLibrary: {
      type: Object,
      default: () => null
    },
    libraryAdmin: {
      type: Boolean,
      default: false
    },
    defaultRetailer: {
      type: Object,
      default: () => null
    },
    retailerAdmin: {
      type: Boolean,
      default: false
    },
    showNoNetworkOptionForLibrary: {
      type: Boolean,
      default: false
    },
    showNoNetworkOptionForRetailer: {
      type: Boolean,
      default: false
    },
    differentiateUserVsRoleDeletion: {
      type: Boolean,
      default: false
    },
    currentUserId: {
      type: Number,
      default: null
    }
  });
  const librariesLoading = ref(false);
  const retailersLoading = ref(false);
  const librariesOptions = ref([]);
  const retailersOptions = ref([]);
  const users = ref([]);
  const showUserModal = ref(false);
  const showDeleteModal = ref(false);
  const showAccessesModal = ref(false);
  const showRemoveAccessModal = ref(false);
  const toastMessage = ref('');
  const selectedUserPristine = ref({
    id: null,
    username: '',
    firstName: '',
    lastName: '',
    mail: ''
  });
  const selectedUser = ref({ ...selectedUserPristine.value });
  const toastMessageType = ref('success'); // 'success' or 'error'
  const filterData = ref({
    selectedRole: null,
    // '' (not null) so it matches the placeholder <option value=""> exactly --
    // otherwise the native <select> can't reflect "nothing selected" and
    // silently defaults its display to another option while the underlying
    // filter stays unset, so no network_id is ever sent to the backend
    // (FBKS-2843, PO-reported: filter visually showed "No network" while the
    // request actually returned the platform_manager's whole own-network list).
    selectedNetwork: '',
    selectedLibrary: { name: '' },
    selectedRetailer: { name: '' },
    searchQuery: '',

    // For the modal
    libraryOptionsModal: {
      options: [],
      loading: false
    },
  });

  const setSelectedUser = (user) => {
    if (user == null) {
      clearSelectedUser();
      return;
    }
    selectedUser.value = {
      id: user.id,
      username: user.username,
      firstName: user.firstName,
      lastName: user.lastName,
      fullName: user.fullName,
      mail: user.mail || user.email,
      role: {
        id: user.role,
        name: props.roles.find(role => role.id === user.role)?.name
      },
      manageNetworks: user.manageNetworks || false,
      manageLibraries: user.manageLibraries || false,
      manageRetailers: user.manageRetailers || false,
      manageCarousels: user.manageCarousels || false
    };
  }

  const clearSelectedUser = () => {
    selectedUser.value = { ...selectedUserPristine.value };
  }

  const toggleUserModal = (user) => {
    user ? setSelectedUser(user) : clearSelectedUser();
    showUserModal.value = !showUserModal.value;
  }

  const menuChanged = (e) => {
    usersManagementTable.value.clearUsers();
    setTimeout(() => {
      usersManagementTable.value.fetchUsers();
    }, 700);
  }

  // Handle library selection from SearchableList component
  const handleLibraryChange = (selected) => {
    filterData.value.selectedLibrary = selected;
    menuChanged();
  };

  // Handle retailer selection from SearchableList component
  const handleRetailerChange = (selected) => {
    filterData.value.selectedRetailer = selected;
    menuChanged();
  };

  // Computed roles according to userType:
  const computedRoles = computed(() => {
    let allowedRolesSet = new Set();

    if (props.userType === 'admin') {
      props.roles.forEach(role => allowedRolesSet.add(role));
    } else {
      if (props.userType === 'platform_manager') {
        allowedRolesSet = new Set(
          props.roles.filter(
            role => role.id !== 'admin' && role.id !== 'support' && role.id !== 'finance'
          )
        );
      }
      const singleRoleTypes = [
        'support',
        'finance',
        'library_manager',
        'retailer_manager'
      ];
      if (singleRoleTypes.includes(props.userType)) {
        const role = props.roles.find(role => role.id === props.userType);
        if (role) allowedRolesSet.add(role);
      }
    }

    return Array.from(allowedRolesSet);
  });

  const showToast = async (e) => {
    toastMessage.value = e.message;
    toastMessageType.value = e.type || 'success';
    const toastEl = document.getElementById('userTest');
    const toast = new bootstrap.Toast(toastEl);
    window.scrollTo(0, 0);
    usersManagementTable.value.fetchUsers();
    // FBKS-4003: let Vue apply the new error/success class first, or its
    // re-render wipes Bootstrap's `show` class and the toast never appears.
    await nextTick();
    toast.show();
  };

  const toggleDeleteModal = (user) => {
    setSelectedUser(user);
    showDeleteModal.value = !showDeleteModal.value;
  }

  const toggleAccessesModal = (user) => {
    setSelectedUser(user);
    showAccessesModal.value = !showAccessesModal.value;
  }

  const toggleRemoveAccessModal = (user) => {
    setSelectedUser(user);
    showRemoveAccessModal.value = !showRemoveAccessModal.value;
  }

  // FBKS-4003: closes the accesses modal once a staff member deleted the account
  // (kept open on error). showToast also refreshes the list.
  const accountDeleted = (e) => {
    if (e.type === 'success') showAccessesModal.value = false;
    showToast(e);
  }

  // FBKS-4003: the library/network/retailer whose access the row button removes,
  // i.e. the filter matching the selected role -- null until one is picked.
  const accessOrganisation = computed(() => {
    const role = filterData.value.selectedRole?.id;
    if (role === 'library_manager' && filterData.value.selectedLibrary?.id) {
      return { role, params: { library_id: filterData.value.selectedLibrary.id }, name: filterData.value.selectedLibrary.name };
    }
    if (role === 'retailer_manager' && filterData.value.selectedRetailer?.id) {
      return { role, params: { retailer_id: filterData.value.selectedRetailer.id }, name: filterData.value.selectedRetailer.name };
    }
    if (role === 'platform_manager' && filterSelectedNetwork.value) {
      return { role, params: { network_id: filterSelectedNetwork.value.id }, name: filterSelectedNetwork.value.name };
    }

    return null;
  });

  const triggerSearch = () => {
    usersManagementTable.value.fetchUsers();
  }

  const handleSearchInput = async () => {
    const shouldSearch = await debounce(filterData.value.searchQuery, 700, 0);
    if (shouldSearch) {
      usersManagementTable.value.clearUsers();
      usersManagementTable.value.fetchUsers();
    }
  };

  const clearSearch = () => {
    filterData.value.searchQuery = '';
    usersManagementTable.value.clearUsers();
    usersManagementTable.value.fetchUsers();
  };

  const clearLibraryAndRetailer = () => {
    filterData.value.selectedLibrary = { name: '' };
    filterData.value.selectedRetailer = { name: '' };
    librariesOptions.value = [];
    retailersOptions.value = [];
  }

  const changedNetwork = async () => {
    if (!filterData.value.selectedNetwork) return;

    clearLibraryAndRetailer();
    await loadLibrariesOrRetailers();
  };

  const loadLibrariesOrRetailers = async () => {
    const type = filterData.value.selectedRole?.id === 'library_manager' ? 'libraries' : 'retailers';
    type === 'libraries' ? librariesLoading.value = true : retailersLoading.value = true;
    const results = await fetchAllLibrariesOrRetailersByNetwork(type, filterData.value.selectedNetwork);
    if (type === 'libraries') {
      librariesOptions.value = results;
      librariesLoading.value = false;
    } else {
      retailersOptions.value = results;
      retailersLoading.value = false;
    }
  };

  // FBKS-3998: preselect the manager's own network (and library/retailer),
  // so they don't have to pick what they can only ever pick anyway.
  const applyDefaultFilters = () => {
    if (!filterData.value.selectedRole) return;

    if (props.userType === 'platform_manager') {
      const networkId = lockedNetworkId.value ?? props.defaultNetwork?.id;
      if (!networkId) return;
      filterData.value.selectedNetwork = networkId;
    } else if (props.userType === 'library_manager' && props.defaultLibrary) {
      filterData.value.selectedNetwork = props.defaultLibrary.network_id ?? 'none';
      filterData.value.selectedLibrary = { id: props.defaultLibrary.id, name: props.defaultLibrary.name };
    } else if (props.userType === 'retailer_manager' && props.defaultRetailer) {
      filterData.value.selectedNetwork = props.defaultRetailer.network_id ?? 'none';
      filterData.value.selectedRetailer = { id: props.defaultRetailer.id, name: props.defaultRetailer.name };
    } else {
      return;
    }

    loadLibrariesOrRetailers();
  };

  // Library/retailer managers only ever get their own role.
  onMounted(() => {
    if (!['library_manager', 'retailer_manager'].includes(props.userType)) return;

    filterData.value.selectedRole = computedRoles.value[0] || null;
    menuChanged();
  });

  const calculateCreateButtonVisibility = () => {
    const allowedUsers = props.userType === 'admin' ||
      (
        (props.networkAdmin == true && props.userType === 'platform_manager') ||
        (props.libraryAdmin == true && props.userType === 'library_manager') ||
        (props.retailerAdmin == true && props.userType === 'retailer_manager')
      );

    return (filterData.value.selectedRole?.id !== undefined && props.userType !== 'user') && allowedUsers;
  };

  // FBKS-3997: a platform manager only ever filters by their own network, so
  // it's preselected and its dropdown hidden.
  const lockedNetworkId = computed(() => {
    return props.userType === 'platform_manager' && props.networks.length === 1 ? props.networks[0].id : null;
  });

  watch(() => filterData.value.selectedRole, (newRole) => {
    filterData.value.selectedNetwork = lockedNetworkId.value ?? '';
    clearLibraryAndRetailer();
    applyDefaultFilters();
  });

  const visibilityByRole = computed(() => {
    const role = filterData.value.selectedRole?.id;
    const visibility = {
      network: false,
      library: false,
      retailer: false
    };

    if (!role) return visibility;

    // Role -> Network -> Library/Retailer cascade, for every
    // network-scoped role, not just Platform Manager.
    if (['platform_manager', 'library_manager', 'retailer_manager'].includes(role)) {
      visibility.network = true;
    }
    if (role === 'library_manager' && filterData.value.selectedNetwork) {
      visibility.library = true;
    }
    if (role === 'retailer_manager' && filterData.value.selectedNetwork) {
      visibility.retailer = true;
    }

    return visibility;
  });

  const filterSelectedNetwork = computed(() => {
    if (!filterData.value.selectedNetwork) return null;
    return props.networks.find((network) => network.id == filterData.value.selectedNetwork) || null;
  });

  // 'none' ("Sans réseau" -- libraries/retailers with no network, FBKS-2843)
  // is a sentinel string, not a real network id -- keep it as-is instead of
  // parseInt()'ing it into NaN.
  const filterSelectedNetworkId = computed(() => {
    if (!filterData.value.selectedNetwork) return null;
    if (filterData.value.selectedNetwork === 'none') return 'none';

    return parseInt(filterData.value.selectedNetwork);
  });

  // Copilot review: showing/labeling this option must depend on the
  // currently selected role, not be one flat switch -- a user can have a
  // networkless retailer but no networkless library (or vice versa), and
  // "Libraries with no network" is a misleading label while filtering
  // retailer_manager.
  const showNoNetworkOption = computed(() => {
    const role = filterData.value.selectedRole?.id;
    if (role === 'library_manager') return props.showNoNetworkOptionForLibrary;
    if (role === 'retailer_manager') return props.showNoNetworkOptionForRetailer;

    return false;
  });

  const noNetworkLabel = computed(() => {
    const role = filterData.value.selectedRole?.id;
    return role === 'retailer_manager' ? 'no_network_retailer' : 'no_network';
  });
</script>

<template>
  <div :class="['align-items-center border-0', toastMessageType === 'error' ? 'toast_error toast' : 'toast']" role="alert" aria-live="assertive" aria-atomic="true" id="userTest">
    <div class="d-flex">
      <div class="toast-body">
        {{ t(`${i18nScope}.${toastMessage}`) }}
      </div>
      <button type="button" class="btn-close btn-close-white me-2 m-auto" data-bs-dismiss="toast" aria-label="Close"></button>
    </div>
  </div>

  <Modal
    :show="showUserModal"
    @close="showUserModal = false"
    :title="selectedUser.id ? t(`${i18nScope}.update_user`): t(`${i18nScope}.create_user`)"
    size="medium"
    :dismissible="false"
  >
    <UserModal
      :userId="1"
      @closeModal="showUserModal = false"
      @showToast="showToast"
      :userType="props.userType"
      :userRoles="computedRoles"
      :defaultRole="filterData.selectedRole"
      :networks="props.networks"
      :defaultNetwork="filterSelectedNetwork"
      :networkAdmin="props.networkAdmin"
      :defaultLibrary="filterData.selectedLibrary"
      :libraryAdmin="props.libraryAdmin"
      :defaultRetailer="filterData.selectedRetailer"
      :retailerAdmin="props.retailerAdmin"
      :libraryOptions="filterData.libraryOptionsModal"
      :selectedUser="selectedUser"
    />
  </Modal>

  <Modal
    :show="showDeleteModal"
    @close="showDeleteModal = false"
    @showToast="showToast"
    :title="'Delete user'"
    size="medium"
  >
    <UserDeleteModal
      :userId="1"
      @showToast="showToast"
      @closeModal="showDeleteModal = false"
      @triggerSearch="triggerSearch"
      :user="selectedUser" />
  </Modal>

  <Modal
    v-if="props.differentiateUserVsRoleDeletion"
    :show="showRemoveAccessModal"
    @close="showRemoveAccessModal = false"
    size="medium"
  >
    <template #title>{{ t(`${i18nScope}.remove_access`) }}</template>
    <UserRemoveAccessModal
      v-if="accessOrganisation"
      @showToast="showToast"
      @closeModal="showRemoveAccessModal = false"
      :user="selectedUser"
      :organisation="accessOrganisation" />
  </Modal>

  <Modal :show="showAccessesModal" @close="showAccessesModal = false" size="medium">
    <template #title>{{ t(`${i18nScope}.accesses.title`, { username: selectedUser.username }) }}</template>
    <UserAccessesModal
      :user="selectedUser"
      :canDeleteAccount="props.differentiateUserVsRoleDeletion"
      @accountDeleted="accountDeleted" />
  </Modal>

  <div class="justify-content-start">

    <div class="row w-100">
      <div class="col-md-4">
        <div class="input-group mb-2">
          <input
            class="form-control"
            :placeholder="`&#x1F50D; &nbsp;` + $t('components.users_management.search_placeholder')"
            :aria-label="$t('components.users_management.search')"
            type="text"
            id="search-input"
            v-model="filterData.searchQuery"
            @input="handleSearchInput"
          />
          <button
            type="button"
            class="btn btn-outline-secondary btn-no-left-margin"
            @click="clearSearch"
            :disabled="!filterData.searchQuery"
            v-if="filterData.searchQuery"
          >×</button>
        </div>
      </div>
      <div class="col-md-8">
        &nbsp;
      </div>
    </div>

    <div class="row w-100 my-4">
      <div class="col-2">
        <label for="role" class="form-label">{{ t(`${i18nScope}.select_a_role`) }}</label>
        <select class="form-select" aria-label="User's role" v-model="filterData.selectedRole" @change="menuChanged">
          <option value="" @click="filterData.selectedRole = null">- {{ t(`${i18nScope}.select_a_role`) }} -</option>
          <option v-for="role in computedRoles" :value="role">
            {{ role.name }}
          </option>
        </select>
      </div>

      <div class="col-2" v-if="!lockedNetworkId">
        <label for="network" class="form-label">{{ t(`${i18nScope}.network`) }}</label>
        <select
          class="form-select"
          aria-label="Network"
          v-model="filterData.selectedNetwork"
          :disabled="!visibilityByRole.network"
          :class="{ 'disabled-select': !visibilityByRole.network }"
          @change="() => {
            menuChanged();
            changedNetwork();
          }"
        >
          <option value="" @click="filterData.selectedNetwork = ''">{{ t(`${i18nScope}.select_a_network`) }}</option>
          <option v-if="showNoNetworkOption" value="none">{{ t(`${i18nScope}.${noNetworkLabel}`) }}</option>
          <option v-for="network in props.networks" :key="network.id" :value="network.id">
            {{ network.name }}
          </option>
        </select>
      </div>

      <div class="col-3" id="libraries_selector" v-if="props.userType !== 'retailer_manager'">
        <label for="library" class="form-label">{{ t(`${i18nScope}.filters.library`) }}</label>
        <SearchableList
          v-model="filterData.selectedLibrary"
          :options="librariesOptions"
          :disabled="!visibilityByRole.library"
          :loading="librariesLoading"
          :placeholder="t(`${i18nScope}.select_a_library`)"
          aria-label="Library"
          :clear-button-label="t(`${i18nScope}.clear_library_selection`)"
          @change="handleLibraryChange"
        />
      </div>

      <div class="col-3" id="retailers_selector" v-if="props.userType !== 'library_manager'">
        <label for="retailer" class="form-label">{{ t(`${i18nScope}.filters.retailer`) }}</label>
        <SearchableList
          v-model="filterData.selectedRetailer"
          :options="retailersOptions"
          :disabled="!visibilityByRole.retailer"
          :loading="retailersLoading"
          :placeholder="t(`${i18nScope}.select_a_retailer`)"
          aria-label="Retailer"
          :clear-button-label="t(`${i18nScope}.clear_retailer_selection`)"
          @change="handleRetailerChange"
        />
      </div>
      <div class="col-2">
        <button
          type="button"
          class="btn btn-primary align-bottom float-end button-offset"
          :disabled="!calculateCreateButtonVisibility()"
          v-on:click="toggleUserModal()">
          {{ t(`${i18nScope}.create_user`) }}
        </button>
      </div>
    </div>

    <hr>

    <UsersManagementTable
      :users="users"
      :userType="props.userType"
      :selectedRole="filterData.selectedRole ? filterData.selectedRole.id : null"
      :selectedLibraryId="filterData.selectedLibrary?.id ? parseInt(filterData.selectedLibrary.id) : null"
      :selectedRetailerId="filterData.selectedRetailer?.id ? parseInt(filterData.selectedRetailer.id) : null"
      :selectedNetworkId="filterSelectedNetworkId"
      :searchQuery="filterData.searchQuery"
      ref="usersManagementTable"
      class="my-4"
      @toggleDeleteModal="toggleDeleteModal"
      @toggleUserModal="toggleUserModal"
      @toggleAccessesModal="toggleAccessesModal"
      :differentiateUserVsRoleDeletion="props.differentiateUserVsRoleDeletion"
      :canRemoveAccess="!!accessOrganisation"
      :currentUserId="props.currentUserId"
      @toggleRemoveAccessModal="toggleRemoveAccessModal"
    />

  </div>
</template>

<style scoped lang="scss">
  :deep() {
    @import '../../../node_modules/bootstrap/scss/bootstrap.scss';
    @import '../../../node_modules/bootstrap-vue-next/dist/bootstrap-vue-next';
  }

  .toast {
    background-color: var(--primary);
    color: white;
  }
  .toast_error {
    background-color: var(--danger);
  }

  .disabled-select {
    opacity: 0.3 !important;
    background-color: white !important;
  }

  .button-offset {
    margin-top: 30px !important;
  }

  .btn-no-left-margin {
    margin-left: 0 !important;
  }
</style>

<i18n src="../locales/en.json" global></i18n>
<i18n src="../locales/fr.json" global></i18n>
<i18n src="../locales/es.json" global></i18n>
<i18n src="../locales/it.json" global></i18n>
<i18n src="../locales/pt.json" global></i18n>
<i18n src="../locales/de.json" global></i18n>
